import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronRight, Plus } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses, statusOf, subtasksOf } from '@/lib/hierarchy';
import { createTask, updateTask } from '@/lib/actions';
import { useOpenTask } from '@/lib/nav';
import { Popover } from '@/components/ui/Popover';
import { openContextMenu } from '@/components/ui/overlays';
import { StatusIcon, StatusMenu, statusProgress } from '@/components/fields/Status';
import { AssigneeCell, DueDateCell, PriorityCell } from '@/components/fields/TaskCells';
import { TaskContextMenu } from './TaskContextMenu';

function SubRow({ task }: { task: Task }) {
  const openTask = useOpenTask();
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  const count = useDb((db) => subtasksOf(db, task.id).length);
  return (
    <div className={clsx('tm-sub-row', { closed: isCompleteType(status.type) })} onClick={() => openTask(task.id)} onContextMenu={(e) => openContextMenu(e, (c) => <TaskContextMenu task={task} close={c} />)}>
      <div className="tm-sub-name">
        <Popover
          content={(close) => (
            <StatusMenu
              statuses={statuses}
              currentId={task.statusId}
              onPick={(s) => {
                updateTask(task.id, { statusId: s.id });
                close();
              }}
            />
          )}
        >
          <button className="lv-status-btn">
            <StatusIcon type={status.type} color={status.color} progress={statusProgress(statuses, status)} />
          </button>
        </Popover>
        <span className="truncate tm-sub-title">{task.name}</span>
        {count > 0 && <span className="faint" style={{ fontSize: 'var(--fs-sm)' }}>⤷ {count}</span>}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <AssigneeCell task={task} size={22} />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <PriorityCell task={task} />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <DueDateCell task={task} />
      </div>
    </div>
  );
}

export function SubtasksSection({ task }: { task: Task }) {
  const tasks = useDb((db) => db.tasks);
  const db = useDb((d) => d);
  const subs = useMemo(() => subtasksOf(db, task.id), [tasks, task.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = subs.filter((t) => !isCompleteType(statusOf(db, t).type)).length;
  const done = subs.length - open;
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const save = () => {
    if (!name.trim()) return;
    createTask({ listId: task.listId, parentId: task.id, name });
    setName('');
  };
  return (
    <section className="tm-section">
      <div className="tm-section-head">
        <button className={`tm-caret ${expanded ? 'open' : ''}`} onClick={() => setExpanded(!expanded)}>
          <ChevronRight size={14} />
        </button>
        <span className="tm-section-title">Subtasks</span>
        {subs.length > 0 && (
          <>
            <span className="faint" style={{ fontSize: 'var(--fs-md)' }}>
              {open} open
            </span>
            <span className="tm-progress">
              <span style={{ width: `${subs.length ? (done / subs.length) * 100 : 0}%` }} />
            </span>
          </>
        )}
        <span className="spacer" />
        <button className="icon-btn sm" onClick={() => (setExpanded(true), setAdding(true))} title="Add subtask">
          <Plus size={15} />
        </button>
      </div>
      {expanded && (subs.length > 0 || adding) && (
        <div className="tm-sub-table">
          <div className="tm-sub-head">
            <span>Name</span>
            <span>Assignee</span>
            <span>Priority</span>
            <span>Due date</span>
          </div>
          {subs.map((s) => (
            <SubRow key={s.id} task={s} />
          ))}
          {adding ? (
            <div className="tm-sub-add editing">
              <StatusIcon type="open" color="var(--text-3)" />
              <input
                autoFocus
                value={name}
                placeholder="Task name"
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save();
                  if (e.key === 'Escape') setAdding(false);
                }}
                onBlur={() => !name && setAdding(false)}
              />
              <button className="btn sm primary" onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={!name.trim()}>
                Save
              </button>
            </div>
          ) : (
            <button className="tm-sub-add" onClick={() => setAdding(true)}>
              <Plus size={14} /> Add Task
            </button>
          )}
        </div>
      )}
    </section>
  );
}
